/**
 * ScreenshotPanelComponent
 *
 * Right-drawer gallery for all screenshots on a takeoff. Pure-presentation —
 * subscribes to ScreenshotsService.screenshots$ for data; mutations call back
 * to the host or directly to the service for delete/bulk-delete.
 *
 * UI states:
 *   - Browse mode: search, page-filter chips, grouped list, per-card actions
 *   - Select mode: checkbox per card, bulk action bar (Share / Export / Delete)
 *
 * Search + page filter are reactive FormControls per the project's
 * mock-data-into-form-controls rule.
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { trigger, transition, style, animate } from '@angular/animations';
import { BehaviorSubject, Subject, combineLatest } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  startWith,
  takeUntil,
} from 'rxjs/operators';

import { Screenshot } from '../screenshots.types';
import { ScreenshotsService } from '../screenshots.service';
import { ToasterService } from 'src/app/services/core/toaster.service';

/** A page-grouped block of screenshots for the template *ngFor. */
export interface ScreenshotPageGroup {
  pageId: string;
  pageName: string;
  items: Screenshot[];
}

@Component({
  standalone: false,
  selector: 'app-screenshot-panel',
  templateUrl: './screenshot-panel.component.html',
  styleUrls: ['./screenshot-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('slideInRight', [
      transition(':enter', [
        style({ transform: 'translateX(100%)', opacity: 0.85 }),
        animate(
          '300ms cubic-bezier(0.25, 0.8, 0.25, 1)',
          style({ transform: 'translateX(0)', opacity: 1 }),
        ),
      ]),
      transition(':leave', [
        animate(
          '220ms cubic-bezier(0.4, 0, 1, 1)',
          style({ transform: 'translateX(100%)', opacity: 0 }),
        ),
      ]),
    ]),
    trigger('fadeBackdrop', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('200ms ease-out', style({ opacity: 1 })),
      ]),
      transition(':leave', [animate('150ms ease-in', style({ opacity: 0 }))]),
    ]),
  ],
})
export class ScreenshotPanelComponent implements OnInit, OnDestroy {
  /** Drives the slide-in / slide-out; bound by host. */
  @Input() open = false;
  /** Optional: current PDF page id so we can highlight matching cards. */
  @Input() currentPageId: string | null = null;
  /**
   * Screenshot whose locate ring the host is currently showing. Forwarded to
   * each card so the matching one can render its locate icon in the active
   * (green) state — used to signal the persistent toggle.
   */
  @Input() activeLocateScreenshotId: string | null = null;

  @Output() closed = new EventEmitter<void>();
  @Output() locateRequest = new EventEmitter<Screenshot>();
  @Output() newCapture = new EventEmitter<void>();
  @Output() annotateRequest = new EventEmitter<Screenshot>();
  @Output() shareRequest = new EventEmitter<Screenshot[]>();

  // ===== Reactive form controls (per "mock data → form controls → HTML bindings") =====
  public searchControl = new FormControl<string>('', { nonNullable: true });
  public pageFilterControl = new FormControl<string | null>(null);

  // ===== Select-mode state =====
  public selectMode = false;
  public selectedIds = new Set<string>();

  // ===== Derived view state =====
  public groups: ScreenshotPageGroup[] = [];
  public pageOptions: { id: string; name: string; count: number }[] = [];
  public totalCount = 0;
  public visibleCount = 0;
  public selectedCount = 0;

  private readonly destroy$ = new Subject<void>();
  private readonly latestScreenshots$ = new BehaviorSubject<Screenshot[]>([]);

  constructor(
    private readonly screenshotsService: ScreenshotsService,
    private readonly cdr: ChangeDetectorRef,
    private readonly toasterService: ToasterService,
  ) {}

  ngOnInit(): void {
    // Wire screenshots stream into the local BehaviorSubject so we can combine
    // with the filter controls in one stream.
    this.screenshotsService.screenshots$
      .pipe(takeUntil(this.destroy$))
      .subscribe((list) => {
        // [SS-FILTER] DEBUG — raw list every time screenshots$ emits.
        // Look for: (a) length matches what you expect, (b) each pageId is
        // a *string* like "1", not a number, and NOT missing/undefined.
        console.log('[SS-FILTER] screenshots$ emit', {
          count: list.length,
          items: list.map((s) => ({
            id: s.id,
            pageId: s.pageId,
            pageIdType: typeof s.pageId,
            pageName: s.pageName,
          })),
        });
        this.latestScreenshots$.next(list);
      });

    combineLatest([
      this.latestScreenshots$,
      this.searchControl.valueChanges.pipe(
        startWith(this.searchControl.value),
        debounceTime(200),
        distinctUntilChanged(),
      ),
      this.pageFilterControl.valueChanges.pipe(
        startWith(this.pageFilterControl.value),
      ),
    ])
      .pipe(
        takeUntil(this.destroy$),
        map(([list, query, pageFilter]) => {
          // [SS-FILTER] DEBUG — verify combineLatest fires when a chip is
          // clicked (should log with a changed pageFilter value).
          console.log('[SS-FILTER] combineLatest tick', {
            listCount: list.length,
            query,
            pageFilter,
            pageFilterType: typeof pageFilter,
          });
          return this.computeView(list, query, pageFilter);
        }),
      )
      .subscribe(({ groups, pageOptions, totalCount, visibleCount }) => {
        // [SS-FILTER] DEBUG — final view state written to the template.
        console.log('[SS-FILTER] view updated', {
          groupCount: groups.length,
          visibleCount,
          totalCount,
          pageOptions: pageOptions.map((p) => ({
            id: p.id,
            idType: typeof p.id,
            count: p.count,
          })),
          groups: groups.map((g) => ({
            pageId: g.pageId,
            itemCount: g.items.length,
          })),
        });
        this.groups = groups;
        this.pageOptions = pageOptions;
        this.totalCount = totalCount;
        this.visibleCount = visibleCount;
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ===== Select-mode handlers =====

  toggleSelectMode(): void {
    this.selectMode = !this.selectMode;
    if (!this.selectMode) {
      this.selectedIds.clear();
      this.selectedCount = 0;
    }
  }

  cancelSelectMode(): void {
    this.selectMode = false;
    this.selectedIds.clear();
    this.selectedCount = 0;
  }

  onCardSelectToggle(screenshot: Screenshot): void {
    if (this.selectedIds.has(screenshot.id)) {
      this.selectedIds.delete(screenshot.id);
    } else {
      this.selectedIds.add(screenshot.id);
    }
    this.selectedCount = this.selectedIds.size;
    this.cdr.markForCheck();
  }

  selectAllVisible(): void {
    for (const g of this.groups) {
      for (const s of g.items) this.selectedIds.add(s.id);
    }
    this.selectedCount = this.selectedIds.size;
    this.cdr.markForCheck();
  }

  // ===== Card actions =====

  onCardLocate(s: Screenshot): void {
    this.locateRequest.emit(s);
  }

  onCardAnnotate(s: Screenshot): void {
    this.annotateRequest.emit(s);
  }

  onCardShare(s: Screenshot): void {
    this.shareRequest.emit([s]);
  }

  onCardDelete(s: Screenshot): void {
    this.screenshotsService.deleteScreenshot(s.id);
    this.selectedIds.delete(s.id);
    this.selectedCount = this.selectedIds.size;
  }

  /** Copy the screenshot image to the system clipboard (falls back to its URL). */
  async onCardCopy(s: Screenshot): Promise<void> {
    try {
      const mode = await this.copyImageToClipboard(s.thumbnail);
      if (mode === 'image') {
        this.toasterService.showSuccessMessage(
          'Screenshot copied successfully',
        );
      } else {
        // Canvas got tainted (S3 CORS) or the browser doesn't expose image
        // clipboard writes — the clipboard now holds a URL, not the picture.
        this.toasterService.showSuccessMessage(
          'Screenshot link copied successfully',
        );
      }
    } catch (err) {
      console.error('[ScreenshotPanel] copy failed', err);
      this.toasterService.showErrorMessage('Could not copy screenshot');
    }
  }

  /**
   * Copies an image (data URL or remote URL) to the clipboard as PNG. Rendering
   * through a canvas normalises the format so the async Clipboard API accepts it;
   * if image copy is unsupported we fall back to copying the source URL as text.
   * Returns `'image'` on success, `'url'` when we had to fall back.
   */
  private async copyImageToClipboard(src: string): Promise<'image' | 'url'> {
    const blob = await this.loadPngBlob(src);
    const canWriteImage =
      typeof ClipboardItem !== 'undefined' && !!navigator.clipboard?.write;
    if (blob && canWriteImage) {
      await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': blob }),
      ]);
      return 'image';
    }
    await navigator.clipboard.writeText(src);
    return 'url';
  }

  private loadPngBlob(src: string): Promise<Blob | null> {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          const ctx = canvas.getContext('2d');
          if (!ctx) return resolve(null);
          ctx.drawImage(img, 0, 0);
          canvas.toBlob((b) => resolve(b), 'image/png');
        } catch {
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = src;
    });
  }

  // ===== Bulk actions =====

  bulkDelete(): void {
    if (this.selectedIds.size === 0) return;
    const ids = Array.from(this.selectedIds);
    this.screenshotsService.deleteScreenshots(ids);
    this.cancelSelectMode();
  }

  bulkShare(): void {
    if (this.selectedIds.size === 0) return;
    const ids = new Set(this.selectedIds);
    const items = this.latestScreenshots$.value.filter((s) => ids.has(s.id));
    this.shareRequest.emit(items);
  }

  /** Page-filter chip clicked. Passing null clears the filter. */
  setPageFilter(pageId: string | null): void {
    // [SS-FILTER] DEBUG — confirm click reaches the handler and that the
    // FormControl value actually flips (compare `before` and `after`).
    const before = this.pageFilterControl.value;
    this.pageFilterControl.setValue(pageId);
    console.log('[SS-FILTER] setPageFilter', {
      clicked: pageId,
      clickedType: typeof pageId,
      before,
      after: this.pageFilterControl.value,
    });
  }

  /** Trigger from the search input's clear button. */
  clearSearch(): void {
    this.searchControl.setValue('');
  }

  /** Convenience for the close button + scrim. */
  close(): void {
    this.closed.emit();
  }

  trackByGroup(_: number, g: ScreenshotPageGroup): string {
    return g.pageId;
  }

  trackByItem(_: number, s: Screenshot): string {
    return s.id;
  }

  /** Compute view-state from raw list + filter controls. Pure function — no side-effects. */
  private computeView(
    list: Screenshot[],
    query: string,
    pageFilter: string | null,
  ): {
    groups: ScreenshotPageGroup[];
    pageOptions: { id: string; name: string; count: number }[];
    totalCount: number;
    visibleCount: number;
  } {
    const trimmed = (query ?? '').trim().toLowerCase();
    // [SS-FILTER] DEBUG — inspect each item's pageId at comparison time. If
    // `matches` is false when you expect true, the two sides on the log line
    // don't agree (probably a type or string-vs-space issue).
    const filtered = list.filter((s) => {
      if (pageFilter) {
        const matches = s.pageId === pageFilter;
        console.log('[SS-FILTER] compare', {
          sPageId: s.pageId,
          sPageIdType: typeof s.pageId,
          pageFilter,
          pageFilterType: typeof pageFilter,
          matches,
        });
        if (!matches) return false;
      }
      if (!trimmed) return true;
      // Include the "P<n>" shorthand alongside the raw page number and full
      // page name so users can search "P1", "1", or "Page 1" interchangeably.
      const pageLabel = s.pageId ? `p${s.pageId}` : '';
      const haystack =
        `${s.caption} ${s.pageName} ${pageLabel} ${s.pageId ?? ''} ${s.linkedMeasurementName ?? ''}`.toLowerCase();
      return haystack.includes(trimmed);
    });

    // Group by pageId preserving first-seen order.
    const groupMap = new Map<string, ScreenshotPageGroup>();
    for (const s of filtered) {
      let g = groupMap.get(s.pageId);
      if (!g) {
        g = { pageId: s.pageId, pageName: s.pageName, items: [] };
        groupMap.set(s.pageId, g);
      }
      g.items.push(s);
    }
    const groups = Array.from(groupMap.values()).sort((a, b) =>
      a.pageId.localeCompare(b.pageId, undefined, { numeric: true }),
    );

    // Page option chips include all pages present in the raw list (not just filtered).
    const optionMap = new Map<
      string,
      { id: string; name: string; count: number }
    >();
    for (const s of list) {
      const cur = optionMap.get(s.pageId);
      if (cur) cur.count++;
      else
        optionMap.set(s.pageId, { id: s.pageId, name: s.pageName, count: 1 });
    }
    const pageOptions = Array.from(optionMap.values()).sort((a, b) =>
      a.id.localeCompare(b.id, undefined, { numeric: true }),
    );

    return {
      groups,
      pageOptions,
      totalCount: list.length,
      visibleCount: filtered.length,
    };
  }
}
